import { ActionType } from '@/actions/actiontypes';
import { Config } from '@/bootstrap/config';
import { DataLoaderServiceUnavailableError } from '@/bootstrap/useDataLoader';
import { stateConfig } from '@/selectors/selectors';
import {
    API_ENVIRONMENT_STORAGE_KEY,
    LAST_SEEN_STATUS_ALERT_STORAGE_KEY,
    LAST_VISITED_NOTIFICATIONS_STORAGE_KEY,
    PREFERENCES_STORAGE_KEY,
    PreferencesState,
    RootState,
} from '@/state';
import { SplashState } from '@/state/SplashState';
import * as idb from 'idb-keyval';
import * as React from 'react';
import { ReactNode } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import parseErrorMessage from './ErrorParser';
import { PreferencesDialog } from '@/components/preferences/PreferencesDialog';

interface Props {
    children: ReactNode;
    statusConfig: Config['status'];
    dispatch: Dispatch;
}
type Feilmelding = {
    id: string;
    innhold: string;
    tittel: string;
    pavirkedeSystemer: { systemId: string; navn: string }[];
};
type StatusCheck = {
    healthCondition: string;
};
enum HealthCheckStatus {
    LOADING,
    HEALTHY,
    UNHEALTHY,
    UNKNOWN,
}

interface ErrorPageProps {
    sign: 'maintenance' | 'crash';
    header: string;
    children: ReactNode;
}

interface StandardErrorPageProps {
    sign: ErrorPageProps['sign'];
    header: string;
    intro: string;
    message: ReactNode;
}

function getSignClass(sign: ErrorPageProps['sign']): string {
    switch (sign) {
        case 'crash':
            return 'c-error-boundary__image';
        case 'maintenance':
            return 'c-error-boundary__maintenance_sign';
        default:
            return 'c-error-boundary__maintenance_sign';
    }
}

/**
 * Standalone mini error boundary that wraps a child component.
 * If the child throws during render, catches it and shows a fallback.
 */
class SafeComponentBoundary extends React.Component<
    { children: ReactNode; fallback?: ReactNode },
    { hasError: boolean }
> {
    state = { hasError: false };
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch(error: unknown) {
        console.warn('SafeComponentBoundary caught error in child:', error);
    }
    render() {
        if (this.state.hasError) {
            return (
                this.props.fallback ?? (
                    <p
                        className="c-error-boundary__paragraph"
                        style={{ color: '#ed1c2e', fontStyle: 'italic' }}
                    >
                        Innstillinger kunne ikke lastes.
                    </p>
                )
            );
        }
        return this.props.children;
    }
}

interface ErrorBoundaryState {
    nvdbStatusIssue: HealthCheckStatus;
    hasError: boolean;
    eventId: string | null;
    errorStackTrace: Error | null;
    showPreferences: boolean;
}

class ErrorBoundary extends React.Component<Props, ErrorBoundaryState> {
    readonly state: ErrorBoundaryState = {
        nvdbStatusIssue: HealthCheckStatus.LOADING,
        hasError: false,
        eventId: null,
        errorStackTrace: null,
        showPreferences: false,
    };

    async getUnresolvedIssue(): Promise<Feilmelding | undefined> {
        const { api, les, vegkart } = this.props.statusConfig;
        const unresolvedIssues: Feilmelding[] = await fetch(
            `${api}/api/v1/feilmeldinger/ulost`
        )
            .then(r => r.json())
            .then(r => r['feilmeldinger']);
        return unresolvedIssues.find(issue =>
            issue.pavirkedeSystemer.some(system =>
                [les, vegkart].includes(system.systemId)
            )
        );
    }
    async getHealthySystem(systemId: string): Promise<boolean> {
        const { api } = this.props.statusConfig;
        const response = await fetch(`${api}/api/v1/systems/${systemId}`);

        if (!response.ok) {
            return false;
        }

        const status: StatusCheck = await response.json();
        return status.healthCondition === 'HEALTHY';
    }

    async ongoingIssues(): Promise<HealthCheckStatus> {
        const { les, vegkart } = this.props.statusConfig;
        try {
            const [issue, lesHealthy, vegkartHealthy] = await Promise.all([
                this.getUnresolvedIssue(),
                this.getHealthySystem(les),
                this.getHealthySystem(vegkart),
            ]);

            const isHealthy = lesHealthy && vegkartHealthy && issue === undefined;
            return isHealthy ? HealthCheckStatus.HEALTHY : HealthCheckStatus.UNHEALTHY;
        } catch (e) {
            console.warn('Failed to retrieve status check from nvdb-status', e);
            return HealthCheckStatus.UNKNOWN;
        }
    }

    componentDidCatch(error: Error) {
        if (error instanceof DataLoaderServiceUnavailableError) {
            this.setState({
                hasError: true,
                errorStackTrace: error,
                nvdbStatusIssue: HealthCheckStatus.UNKNOWN,
            });
            return;
        }

        this.setState({ nvdbStatusIssue: HealthCheckStatus.LOADING });
        this.ongoingIssues()
            .then(nvdbStatusIssue => this.setState({ nvdbStatusIssue }))
            .catch(() => {
                if (this.state.nvdbStatusIssue === HealthCheckStatus.LOADING) {
                    this.setState({ nvdbStatusIssue: HealthCheckStatus.UNKNOWN });
                }
            });
        this.setState({
            hasError: true,
            errorStackTrace: error,
        });

        const preferencesState = localStorage.getItem(PREFERENCES_STORAGE_KEY);
        const version = localStorage.getItem('version');
        const lastVisited = localStorage.getItem(LAST_VISITED_NOTIFICATIONS_STORAGE_KEY);
        const lastSeenAlert = localStorage.getItem(LAST_SEEN_STATUS_ALERT_STORAGE_KEY);
        const apiEnvironment = sessionStorage.getItem(API_ENVIRONMENT_STORAGE_KEY);

        localStorage.clear();
        sessionStorage.clear();

        if (preferencesState) {
            localStorage.setItem(PREFERENCES_STORAGE_KEY, preferencesState);
        }
        if (version) {
            localStorage.setItem('version', version);
        }
        if (lastVisited) {
            localStorage.setItem(LAST_VISITED_NOTIFICATIONS_STORAGE_KEY, lastVisited);
        }
        if (lastSeenAlert) {
            localStorage.setItem(LAST_SEEN_STATUS_ALERT_STORAGE_KEY, lastSeenAlert);
        }
        if (apiEnvironment) {
            sessionStorage.setItem(API_ENVIRONMENT_STORAGE_KEY, apiEnvironment);
        }

        idb.clear().then(() =>
            console.log('Cleared Indexed DB to prevent preserving corrupt data.')
        );
    }
    handleReloadHomePage = () => {
        window.location.href = '/';
    };

    handleClearStorageAndReload = () => {
        localStorage.clear();
        sessionStorage.clear();
        idb.clear().then(() => {
            console.log('Cleared all storage.');
            window.location.reload();
        });
    };

    handleReloadPage = () => {
        window.location.reload();
    };

    private loadStoredPreferences(): PreferencesState {
        const rawPreferences = localStorage.getItem(PREFERENCES_STORAGE_KEY);
        if (!rawPreferences) {
            return new PreferencesState();
        }
        try {
            const parsed = JSON.parse(rawPreferences);
            return new PreferencesState().override(parsed);
        } catch (error) {
            console.warn(
                'Failed to parse stored preferences. Falling back to defaults.',
                error
            );
            return new PreferencesState();
        }
    }

    handleOpenPreferences = () => {
        const storedPreferences = this.loadStoredPreferences();
        this.props.dispatch({
            type: ActionType.PREFERENCES_UPDATE,
            ...storedPreferences,
        });
        this.props.dispatch({
            type: ActionType.SHOW_SPLASH,
            state: SplashState.PREFERENCES,
        });
        this.setState({ showPreferences: true });
    };

    handleClosePreferences = (splashState: SplashState) => {
        this.props.dispatch({
            type: ActionType.SHOW_SPLASH,
            state: splashState,
        });
        if (splashState === SplashState.NONE) {
            this.setState({ showPreferences: false });
        }
    };

    renderErrorActions() {
        return (
            <div className="c-error-boundary__actions">
                <button
                    className="c-error-boundary__button c-error-boundary__button--primary"
                    onClick={this.handleReloadPage}
                    aria-label="Oppdater siden"
                >
                    Oppdater siden
                </button>
                <button
                    className="c-error-boundary__button"
                    onClick={this.handleReloadHomePage}
                    aria-label="Gå til forsiden"
                >
                    Gå til forsiden
                </button>
                <button
                    className="c-error-boundary__button c-error-boundary__button--danger"
                    onClick={this.handleClearStorageAndReload}
                    aria-label="Nullstill instillinger og oppdater"
                >
                    Nullstill lagring og oppdater
                </button>
                <button
                    className="c-error-boundary__button"
                    onClick={this.handleOpenPreferences}
                    aria-label="Åpne innstillinger"
                >
                    ⚙ Innstillinger
                </button>
            </div>
        );
    }

    renderErrorDetails(message: ReactNode) {
        return (
            <div className="c-error-boundary__text-box" aria-label="Felt for feilmelding">
                <p className="c-error-boundary__paragraph" tabIndex={3}>
                    {message}
                </p>
                <div className="c-error-boundary__details">
                    <h2 className="c-error-boundary__subheading">
                        Sjekk status her:{' '}
                        <a
                            href={this.props.statusConfig.url}
                            aria-label="Knapp for NVDB Status"
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            NVDB Status
                        </a>
                    </h2>
                    <h2 className="c-error-boundary__subheading">
                        Kontakt support her:{' '}
                        <a
                            href="https://www.vegvesen.no/fag/teknologi/nasjonal-vegdatabank/kontakt-nvdb/"
                            aria-label="Kontakt NVDB support"
                            target="_blank"
                            rel="noreferrer noopener"
                        >
                            Kontakt NVDB
                        </a>
                    </h2>
                </div>
            </div>
        );
    }

    renderStandardErrorPage({ sign, header, intro, message }: StandardErrorPageProps) {
        return (
            <div className="c-error-boundary">
                <div className={getSignClass(sign)} />
                <div className="c-error-boundary__content">
                    <div
                        className="c-error-boundary__text-box"
                        aria-label="Felt for informasjon om sideoppdatering"
                    >
                        <h1
                            className="c-error-boundary__heading"
                            tabIndex={1}
                            aria-label="Det har skjedd en ulykke"
                        >
                            {header}
                        </h1>
                        <p className="c-error-boundary__paragraph" tabIndex={2}>
                            {intro}
                        </p>
                        {this.renderErrorActions()}
                    </div>
                    {this.state.showPreferences && (
                        <SafeComponentBoundary>
                            <PreferencesDialog
                                handleWindowChange={this.handleClosePreferences}
                            />
                        </SafeComponentBoundary>
                    )}
                    {this.renderErrorDetails(message)}
                </div>
            </div>
        );
    }

    renderGenericError() {
        return this.renderStandardErrorPage({
            sign: 'crash',
            header: 'Noe har gått galt!',
            intro: 'Prøv en av handlingene nedenfor for å løse problemet:',
            message: parseErrorMessage(
                this.state.errorStackTrace ?? new Error('Unknown error')
            ),
        });
    }

    renderHealthCheckFailed() {
        return this.renderStandardErrorPage({
            sign: 'maintenance',
            header: 'Vegkart er utilgjengelig',
            intro: 'Vegkart kan ikke vise vegdata for øyeblikket. Prøv en av handlingene nedenfor:',
            message: 'Det ser ut til å være et problem med NVDB sine tjenester.',
        });
    }

    renderServiceUnavailableError() {
        return this.renderStandardErrorPage({
            sign: 'maintenance',
            header: 'Vegkart er utilgjengelig',
            intro: 'Vegkart kunne ikke laste inn data ved oppstart. Prøv en av handlingene nedenfor:',
            message: 'Tjenesten svarte med 503 Service Unavailable. Prøv på nytt senere.',
        });
    }

    render() {
        const { hasError, nvdbStatusIssue } = this.state;
        const isServiceUnavailableError =
            this.state.errorStackTrace instanceof DataLoaderServiceUnavailableError;

        if (isServiceUnavailableError) return this.renderServiceUnavailableError();
        if (nvdbStatusIssue == HealthCheckStatus.LOADING && hasError) return null;
        else if (nvdbStatusIssue === HealthCheckStatus.UNHEALTHY)
            return this.renderHealthCheckFailed();
        else if (hasError) return this.renderGenericError();
        else return this.props.children;
    }
}
export default connect((state: RootState) => ({
    statusConfig: stateConfig(state)?.status,
}))(ErrorBoundary);
